import React, { useState, useEffect, useRef, useCallback } from "react";
import { useWebSocket } from "../../../contexts/WebSocketContext";
import { apiClient } from "../../../services/api/apiService";
import ConfirmModal from "../../ui/ConfirmModal";
import { normalizeLeagueStatus } from "../leagueStatus";
import { isSystemAdmin } from "../../../utils/rbac";
import "../messages.css";

/** The closed-chat notice for a finished league, or null while the chat is open. */
export const chatClosedNotice = (league) => {
  const status = normalizeLeagueStatus(league?.status);
  if (status === "completed") return "This league has finished: the chat is closed.";
  if (status === "cancelled") return "This league was cancelled: the chat is closed.";
  return null;
};

/** Error text for an announcement email subject, or null when it is fine. */
export const announcementSubjectError = (subject) => {
  const value = String(subject || "").trim();
  if (value.length < 3) return "Add an email subject (at least 3 characters).";
  if (value.length > 120) return "The email subject can be at most 120 characters.";
  return null;
};

const MessagesTab = ({ leagueId, league = null, currentUser, canEmailAll = false }) => {
  const webSocket = useWebSocket();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  // Host only: also email everyone in the league
  const [emailAll, setEmailAll] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [sentNotice, setSentNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState("");
  const [hasJoinedLeague, setHasJoinedLeague] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, messageId: null, isLoading: false });
  const [serverNotice, setServerNotice] = useState(null);
  const messagesEndRef = useRef(null);

  // Completed and cancelled leagues keep their chat read only; platform admins may still post
  const closedNotice = serverNotice || chatClosedNotice(league);
  const canWrite = !closedNotice || isSystemAdmin(currentUser);

  // Scroll to bottom when messages update
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Newest page first from the API, shown oldest → newest so new
      // messages appended at the bottom keep the order (MS-B12).
      const response = await apiClient.get(`/messages/${leagueId}`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          sort: "DESC",
        },
      });

      const data = response.data;
      setMessages([...(data.messages || [])].reverse());
      setPagination((prev) => data.pagination || prev);
      setServerNotice(data.chat?.closed ? data.chat.notice : null);
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
    }
  }, [leagueId, pagination.page, pagination.limit]);
  
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    if (!leagueId || !webSocket.connected || hasJoinedLeague) return;

    const joined = webSocket.joinLeague(leagueId);
    if (joined) {
      setHasJoinedLeague(true);
    }

    return () => {
      if (leagueId && hasJoinedLeague) {
        webSocket.leaveLeague(leagueId);
        setHasJoinedLeague(false);
      }
    };
  }, [leagueId, webSocket.connected, hasJoinedLeague, webSocket]);

  useEffect(() => {
    if (!leagueId || !webSocket.connected) return;

    const removeCreatedListener = webSocket.addEventListener(
      "league_message_created",
      (data) => {
        if (parseInt(data.leagueId) !== parseInt(leagueId)) return;
        if (!data.message) return;

        setMessages((prev) => {
          if (prev.some((message) => message.id === data.message.id)) {
            return prev;
          }
          return [...prev, data.message];
        });

        setPagination((prev) => ({
          ...prev,
          total: prev.total + 1,
        }));
      }
    );

    const removeUpdatedListener = webSocket.addEventListener(
      "league_message_updated",
      (data) => {
        if (parseInt(data.leagueId) !== parseInt(leagueId)) return;
        if (!data.message) return;

        setMessages((prev) =>
          prev.map((message) =>
            message.id === data.message.id ? data.message : message
          )
        );
      }
    );

    const removeDeletedListener = webSocket.addEventListener(
      "league_message_deleted",
      (data) => {
        if (parseInt(data.leagueId) !== parseInt(leagueId)) return;
        if (!data.messageId) return;

        setMessages((prev) =>
          prev.filter((message) => message.id !== data.messageId)
        );

        setPagination((prev) => ({
          ...prev,
          total: Math.max(0, prev.total - 1),
        }));
      }
    );

    return () => {
      removeCreatedListener();
      removeUpdatedListener();
      removeDeletedListener();
    };
  }, [leagueId, webSocket.connected, webSocket]);



  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim()) return;
    const subjectError = emailAll ? announcementSubjectError(emailSubject) : null;
    if (subjectError) {
      setError(subjectError);
      return;
    }

    try {
      setError(null);
      setSentNotice(null);
      const response = await apiClient.post(`/messages/${leagueId}`, {
        content: newMessage,
        ...(emailAll ? { email_all: true, subject: emailSubject.trim() } : {}),
      });

      const data = response.data;

      setMessages((prev) => {
        if (prev.some((message) => message.id === data.message.id)) {
          return prev;
        }
        return [...prev, data.message];
      });
      setNewMessage("");
      if (emailAll) {
        const count = data.emailed?.recipients ?? 0;
        setSentNotice(data.emailed?.error || `Posted and emailed to ${count} ${count === 1 ? "person" : "people"}.`);
        setEmailAll(false);
        setEmailSubject("");
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      // The league finished while the page was open
      if (err.response?.status === 409) {
        setServerNotice(message);
        return;
      }
      setError(message);
      console.error("Error sending message:", err);
    }
  };

  const handleEditMessage = async (messageId) => {
    if (!editingContent.trim()) return;

    try {
      const response = await apiClient.put(`/messages/${leagueId}/${messageId}`, {
        content: editingContent,
      });

      const data = response.data;

      setMessages(
        messages.map((m) => (m.id === messageId ? data.message : m))
      );
      setEditingId(null);
      setEditingContent("");
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      console.error("Error updating message:", err);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    setDeleteConfirm({ isOpen: true, messageId, isLoading: false });
  };

  const confirmDeleteMessage = async () => {
    if (!deleteConfirm.messageId) return;
    setDeleteConfirm((prev) => ({ ...prev, isLoading: true }));

    try {
      await apiClient.delete(`/messages/${leagueId}/${deleteConfirm.messageId}`);
      setMessages(messages.filter((m) => m.id !== deleteConfirm.messageId));
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      console.error("Error deleting message:", err);
    } finally {
      setDeleteConfirm({ isOpen: false, messageId: null, isLoading: false });
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading && messages.length === 0) {
    return <div className="messages-container">Loading messages...</div>;
  }

  return (
    <div className="messages-tab">
      {error && <div className="messages-error">{error}</div>}

      <div className="messages-list">
        {messages.length === 0 ? (
          closedNotice ? (
            <div className="no-messages">
              <p>No messages were posted in this league.</p>
            </div>
          ) : (
            // The welcome counts as the first message: the composer invites the next one
            <div className="message-item message-item--welcome">
              <div className="message-header">
                <div className="message-user">
                  <div className="message-avatar-placeholder">L</div>
                  <span className="message-username">League Admin</span>
                </div>
              </div>
              <div className="message-content">
                <p>
                  Welcome to the league team chat. Share updates, coordinate games, and have fun.
                </p>
              </div>
            </div>
          )
        ) : (
          messages.map((message) => (
            <div key={message.id} className="message-item">
              <div className="message-header">
                <div className="message-user">
                  {message.avatar_url ? (
                    <img
                      src={message.avatar_url}
                      alt={message.user_name}
                      className="message-avatar"
                    />
                  ) : (
                    <div className="message-avatar-placeholder">
                      {message.user_name?.charAt(0) || "?"}
                    </div>
                  )}
                  <span className="message-username">{message.user_name}</span>
                  <span className="message-time">
                    {formatTime(message.created_at)}
                  </span>
                  {message.updated_at !== message.created_at && (
                    <span className="message-edited">(edited)</span>
                  )}
                  {message.metadata?.type === "announcement" && (
                    <span className="message-announcement-badge" title="Also emailed to everyone in the league">📧 Emailed to all</span>
                  )}
                </div>

                {canWrite && currentUser && currentUser.id === message.user_id && (
                  <div className="message-actions">
                    <button
                      onClick={() => {
                        setEditingId(message.id);
                        setEditingContent(message.content);
                      }}
                      className="message-btn message-btn-edit"
                      title="Edit message"
                    >
                      ✎
                    </button>
                    <button
                      onClick={() => handleDeleteMessage(message.id)}
                      className="message-btn message-btn-delete"
                      title="Delete message"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              <div className="message-content">
                {editingId === message.id ? (
                  <div className="message-edit-form">
                    <textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      className="message-edit-input"
                    />
                    <div className="message-edit-actions">
                      <button
                        onClick={() => handleEditMessage(message.id)}
                        className="btn btn-sm btn-primary"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="btn btn-sm btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p>{message.content}</p>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {closedNotice && (
        <div className="messages-closed" role="status">
          🔒 {closedNotice}
        </div>
      )}

      {canWrite && (
      <form onSubmit={handleSendMessage} className="message-input-form">
        {canEmailAll && (
          <div className="message-email-all">
            <label className="message-email-all__toggle">
              <input
                type="checkbox"
                checked={emailAll}
                onChange={(e) => setEmailAll(e.target.checked)}
              />
              📧 Also email everyone in this league
            </label>
            {emailAll && (
              <input
                type="text"
                className="message-email-all__subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject, e.g. No games this Tuesday"
                maxLength={120}
                aria-label="Email subject"
              />
            )}
            {emailAll && (
              <span className="message-email-all__hint">
                Goes to every player and captain. You can email everyone twice a day; replies come to your email.
              </span>
            )}
          </div>
        )}
        {sentNotice && <div className="message-email-all__sent" role="status">{sentNotice}</div>}
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={messages.length === 0
            ? "Be the first to post a message… (max 5000 characters)"
            : "Type a message... (max 5000 characters)"}
          maxLength="5000"
          className="message-input"
          rows="3"
          aria-label="New message"
        />
        <div className="message-input-footer">
          <span className="character-count">
            {newMessage.length}/5000
          </span>
          <button
            type="submit"
            disabled={!newMessage.trim() || loading}
            className="btn btn-primary"
          >
            {emailAll ? "Send and email all" : "Send Message"}
          </button>
        </div>
      </form>
      )}

      {pagination.pages > 1 && (
        <div className="messages-pagination">
          <button
            onClick={() =>
              setPagination({ ...pagination, page: pagination.page - 1 })
            }
            disabled={pagination.page === 1}
            className="btn btn-sm"
          >
            ← Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() =>
              setPagination({ ...pagination, page: pagination.page + 1 })
            }
            disabled={pagination.page === pagination.pages}
            className="btn btn-sm"
          >
            Next →
          </button>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Message?"
        message="Are you sure you want to delete this message?"
        confirmText="Delete"
        onConfirm={confirmDeleteMessage}
        onCancel={() => setDeleteConfirm({ isOpen: false, messageId: null, isLoading: false })}
        isLoading={deleteConfirm.isLoading}
        isDanger={true}
      />
    </div>
  );
};

export default MessagesTab;
